import { defineComponent as _defineComponent } from 'vue';
import { resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, createVNode as _createVNode, unref as _unref, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, withModifiers as _withModifiers } from "vue";
const _hoisted_1 = { class: "patient-case-picker" };
const _hoisted_2 = {
    key: 2,
    class: "text-body-2 text-medium-emphasis mt-2 mb-0"
};
const _hoisted_3 = { class: "d-flex align-center ga-3" };
const _hoisted_4 = { class: "flex-grow-1" };
const _hoisted_5 = { class: "text-body-1 font-weight-bold" };
const _hoisted_6 = { class: "text-body-2 text-medium-emphasis" };
const _hoisted_7 = {
    key: 4,
    class: "mt-3"
};
const _hoisted_8 = {
    key: 0,
    class: "text-body-2 text-medium-emphasis mb-0"
};
const _hoisted_9 = { class: "d-flex align-center ga-3" };
const _hoisted_10 = { class: "flex-grow-1" };
const _hoisted_11 = {
    key: 0,
    class: "text-caption text-medium-emphasis mt-2 mb-0"
};
import { computed, onMounted, ref } from "vue";
import JourneyLane from "@/components/missionControl/JourneyLane.vue";
import { useFilterDebounce } from "@/composables/useFilterDebounce";
import { PatientGridRow, pipelineFor, sharedColumns, shortDate } from "@/models/missionControl";
import { missionControlService } from "@/services/missionControlService";
import { fullName } from "@/utils/person";
/**
 * Picks the patient and one of the patient's open cases. Without `patient`, the user searches for one.
 * With `patient`, the patient is fixed and only the case is picked. The value is null until the choice is complete.
 */
const SEARCH_DEBOUNCE_MS = 1000;
// Mirrors MAX_CASES_PER_PATIENT in backend/portal/logic/mission_control/grid.py; the grid's default of 3 would hide
// a patient's later open cases from the picker.
const CASES_PER_PATIENT = 10;
export default /*@__PURE__*/ _defineComponent({
    __name: 'PatientCasePicker',
    props: {
        modelValue: {},
        patient: {}
    },
    emits: ["update:modelValue"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const search = ref("");
        const rows = ref([]);
        const visibleModules = ref([]);
        const selectedId = ref(null);
        const loading = ref(false);
        const error = ref(null);
        let latestLoad = 0;
        const pipeline = computed(() => pipelineFor(visibleModules.value));
        const selectedRow = computed(() => rows.value.find((row) => row.id === selectedId.value) ?? null);
        const journeyColumns = computed(() => sharedColumns(selectedRow.value?.lanes ?? [], pipeline.value));
        const toPatient = (row) => ({
            id: row.id,
            firstName: row.raw.firstName,
            lastName: row.raw.lastName,
            mrn: row.mrn,
        });
        const load = async (params) => {
            const attempt = ++latestLoad;
            loading.value = true;
            error.value = null;
            try {
                const page = await missionControlService.patients({ ...params, casesPerPatient: CASES_PER_PATIENT });
                if (attempt !== latestLoad)
                    return;
                visibleModules.value = page.visibleModules ?? [];
                rows.value = PatientGridRow.fromPage(page);
            }
            catch {
                if (attempt === latestLoad)
                    error.value = "Patients could not load. Try again.";
            }
            finally {
                if (attempt === latestLoad)
                    loading.value = false;
            }
        };
        const choosePatient = (id) => {
            selectedId.value = id;
            const row = selectedRow.value;
            // A patient with no open case gets the files as patient documents. Otherwise a case must be picked.
            emit("update:modelValue", row && !row.lanes.length ? { patient: toPatient(row), caseId: null } : null);
        };
        const chooseCase = (caseId) => {
            if (!selectedRow.value)
                return;
            emit("update:modelValue", { patient: toPatient(selectedRow.value), caseId });
        };
        // The clearable field sets null when cleared
        useFilterDebounce(search, (term) => {
            choosePatient(null);
            const trimmed = (term ?? "").trim();
            if (!trimmed) {
                rows.value = [];
                return;
            }
            void load({ search: trimmed, scope: "all" });
        }, SEARCH_DEBOUNCE_MS);
        onMounted(async () => {
            if (!props.patient)
                return;
            // Mission Control hands over a fixed patient. The exact patient filter returns only their row.
            await load({ patient: props.patient.id, scope: "all" });
            if (error.value)
                return;
            const row = rows.value[0];
            if (row)
                choosePatient(row.id);
            else
                emit("update:modelValue", { patient: props.patient, caseId: null });
        });
        return (_ctx, _cache) => {
            const _component_v_text_field = _resolveComponent("v-text-field");
            const _component_v_alert = _resolveComponent("v-alert");
            const _component_v_icon = _resolveComponent("v-icon");
            const _component_v_chip = _resolveComponent("v-chip");
            const _component_v_card = _resolveComponent("v-card");
            const _component_v_item = _resolveComponent("v-item");
            const _component_v_item_group = _resolveComponent("v-item-group");
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                (!__props.patient)
                    ? (_openBlock(), _createBlock(_component_v_text_field, {
                        key: 0,
                        modelValue: search.value,
                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((search).value = $event)),
                        label: "Find the patient",
                        hint: "Name, date of birth or Patient ID (MRN)",
                        "persistent-hint": "",
                        "prepend-inner-icon": "mdi-magnify",
                        density: "comfortable",
                        clearable: "",
                        loading: loading.value,
                        "aria-label": "Find the patient"
                    }, null, 8, ["modelValue", "loading"]))
                    : _createCommentVNode("", true),
                (error.value)
                    ? (_openBlock(), _createBlock(_component_v_alert, {
                        key: 1,
                        type: "error",
                        variant: "tonal",
                        density: "compact",
                        class: "mt-2"
                    }, {
                        default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(error.value), 1)
                        ]),
                        _: 1
                    }))
                    : (!loading.value && search.value?.trim() && !rows.value.length)
                        ? (_openBlock(), _createElementBlock("p", _hoisted_2, " No patient matches. Try the name, date of birth or Patient ID (MRN). "))
                        : _createCommentVNode("", true),
                (!__props.patient && rows.value.length)
                    ? (_openBlock(), _createBlock(_component_v_item_group, {
                        key: 3,
                        class: "d-flex flex-column ga-2 mt-2",
                        "model-value": selectedId.value,
                        "aria-label": "Patients",
                        "onUpdate:modelValue": _cache[1] || (_cache[1] = (id) => choosePatient(id ?? null))
                    }, {
                        default: _withCtx(() => [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(rows.value, (row) => {
                                return (_openBlock(), _createBlock(_component_v_item, {
                                    key: row.id,
                                    value: row.id
                                }, {
                                    default: _withCtx(({ isSelected, toggle }) => [
                                        _createVNode(_component_v_card, {
                                            flat: "",
                                            class: _normalizeClass(["pa-3 picker-card", { 'picker-card--selected': isSelected }]),
                                            "aria-checked": isSelected,
                                            role: "radio",
                                            onClick: toggle
                                        }, {
                                            default: _withCtx(() => [
                                                _createElementVNode("div", _hoisted_3, [
                                                    _createVNode(_component_v_icon, {
                                                        icon: isSelected ? 'mdi-radiobox-marked' : 'mdi-radiobox-blank',
                                                        color: "primary"
                                                    }, null, 8, ["icon"]),
                                                    _createElementVNode("div", _hoisted_4, [
                                                        _createElementVNode("div", _hoisted_5, _toDisplayString(_unref(fullName)(row.raw)), 1),
                                                        _createElementVNode("div", _hoisted_6, " DOB " + _toDisplayString(_unref(shortDate)(row.dob)) + " · MRN " + _toDisplayString(row.mrn || "none"), 1)
                                                    ]),
                                                    (row.openWork)
                                                        ? (_openBlock(), _createBlock(_component_v_chip, {
                                                            key: 0,
                                                            size: "small",
                                                            variant: "tonal",
                                                            color: "primary"
                                                        }, {
                                                            default: _withCtx(() => [
                                                                _createTextVNode(_toDisplayString(row.openWork) + " open", 1)
                                                            ]),
                                                            _: 2
                                                        }, 1024))
                                                        : _createCommentVNode("", true)
                                                ])
                                            ]),
                                            _: 2
                                        }, 1032, ["class", "aria-checked", "onClick"])
                                    ]),
                                    _: 2
                                }, 1032, ["value"]));
                            }), 128))
                        ]),
                        _: 1
                    }, 8, ["model-value"]))
                    : _createCommentVNode("", true),
                (selectedRow.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                        (!selectedRow.value.lanes.length)
                            ? (_openBlock(), _createElementBlock("p", _hoisted_8, " Added to the patient's documents. This patient has no open case. "))
                            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                _cache[3] || (_cache[3] = _createElementVNode("p", { class: "text-body-2 font-weight-bold mb-2" }, "Which open case?", -1)),
                                _createVNode(_component_v_item_group, {
                                    class: "d-flex flex-column ga-2",
                                    "model-value": __props.modelValue?.caseId ?? null,
                                    mandatory: "",
                                    "aria-label": "Open cases",
                                    "onUpdate:modelValue": _cache[2] || (_cache[2] = (caseId) => chooseCase(caseId))
                                }, {
                                    default: _withCtx(() => [
                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selectedRow.value.lanes, (lane) => {
                                            return (_openBlock(), _createBlock(_component_v_item, {
                                                key: lane.caseId,
                                                value: lane.caseId
                                            }, {
                                                default: _withCtx(({ isSelected, toggle }) => [
                                                    _createVNode(_component_v_card, {
                                                        flat: "",
                                                        class: _normalizeClass(["pa-3 picker-card", { 'picker-card--selected': isSelected }]),
                                                        onClickCapture: _withModifiers(toggle, ["prevent"])
                                                    }, {
                                                        default: _withCtx(() => [
                                                            _createElementVNode("div", _hoisted_9, [
                                                                _createVNode(_component_v_icon, {
                                                                    icon: isSelected ? 'mdi-radiobox-marked' : 'mdi-radiobox-blank',
                                                                    color: "primary"
                                                                }, null, 8, ["icon"]),
                                                                _createElementVNode("div", _hoisted_10, [
                                                                    _createVNode(JourneyLane, {
                                                                        mini: "",
                                                                        lane: lane,
                                                                        columns: journeyColumns.value,
                                                                        pipeline: pipeline.value
                                                                    }, null, 8, ["lane", "columns", "pipeline"])
                                                                ])
                                                            ])
                                                        ]),
                                                        _: 2
                                                    }, 1032, ["class", "onClickCapture"])
                                                ]),
                                                _: 2
                                            }, 1032, ["value"]));
                                        }), 128))
                                    ]),
                                    _: 1
                                }, 8, ["model-value"]),
                                (selectedRow.value.moreLanes)
                                    ? (_openBlock(), _createElementBlock("p", _hoisted_11, " +" + _toDisplayString(selectedRow.value.moreLanes) + " more open ", 1))
                                    : _createCommentVNode("", true)
                            ], 64))
                    ]))
                    : _createCommentVNode("", true)
            ]));
        };
    }
});
